import { Box, IconButton, TextField, Typography, Switch, FormControlLabel } from "@mui/material";
import { Modal, ModalBody } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { UsernameSelectField, styles } from "../../pages/MyTracks/styles";
import { useStyles } from "../../pages/MyTracks/styles";
import {
  GetEnterpriseAllUsersResponse,
  AddCollaboratorsToProjectsResponse,
  UpdateProjectResponse,
} from "../../proto/api_pb";
import { Collaborator, Project, User } from "../../proto/api_pb";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { collaboratorsModalStyles, shareModalStyles } from "./styles";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  BORDER_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { addCollaboratorsToProjects, updateProject } from "../../services/projectService";
import CustomButton from "../CustomButton/CustomButton";

interface BatchEditProjectModalProps {
  open: boolean;
  onClose: () => void;
  selectedProjectIds: Set<string>;
  projects: Project[];
  onSuccess: () => void;
}

const BatchEditProjectModal: React.FC<BatchEditProjectModalProps> = ({
  open,
  onClose,
  selectedProjectIds,
  projects,
  onSuccess,
}) => {
  const classes = useStyles();
  const [collabVal, setCollabVal] = useState("");
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const { userName } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [userNames, setUserNames] = useState<string[]>([]);
  const { enterprise } = useContext(UserDetailsContext);

  const [privacyEnabled, setPrivacyEnabled] = useState<boolean>(false);
  const [makePrivate, setMakePrivate] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      setCollaborators([]);
      setCollabVal("");
      setPrivacyEnabled(false);
      setMakePrivate(true);
    }
  }, [open]);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          newCollab.setProfilePicture(addedCollab.getProfilePicture());
          setCollaborators([...collaborators, newCollab]);
        }
        setCollabVal("");
      } else {
        toast.info("Username does not exist or already added");
      }
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item.getUserId() !== collaborator.getUserId()
    );
    setCollaborators(updatedCollaborators);
  };

  const handleConfirm = async () => {
    const hasCollabChanges = collaborators.length > 0;
    const hasPrivacyChanges = privacyEnabled;

    if (!hasCollabChanges && !hasPrivacyChanges) {
      toast.error("Please make at least one change");
      return;
    }
    if (selectedProjectIds.size === 0) {
      toast.error("No projects selected");
      return;
    }

    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const projectIds = Array.from(selectedProjectIds);

      // Handle collaborator additions via batch API
      if (hasCollabChanges) {
        const response = await addCollaboratorsToProjects(
          token,
          projectIds,
          collaborators,
          enterprise?.id ?? ""
        );

        if (response instanceof RpcError) {
          toast.error("Failed to add collaborators");
          throw response;
        }

        if (response instanceof AddCollaboratorsToProjectsResponse) {
          const failed = response.getFailedProjectIdsList();
          if (failed.length > 0) {
            toast.warning(`Added collaborators to ${response.getUpdatedProjectIdsList().length} projects, ${failed.length} failed`);
          }
        }
      }

      // Handle privacy updates
      if (hasPrivacyChanges) {
        let privacyFailures = 0;
        for (const pId of projectIds) {
          const project = projects.find((p) => p.getId() === pId);
          if (!project) {
            privacyFailures++;
            continue;
          }
          try {
            project.setPrivate(makePrivate);
            project.setModifiedTimestamp(Date.now());
            const res = await updateProject(token, project);
            if (!(res instanceof UpdateProjectResponse)) privacyFailures++;
          } catch (error) {
            console.error(`Failed to update project ${pId}:`, error);
            privacyFailures++;
          }
        }
        if (privacyFailures > 0) {
          toast.warn(`Privacy update failed for ${privacyFailures} project${privacyFailures !== 1 ? "s" : ""}`);
        }
      }

      const changeDesc = [
        hasCollabChanges ? "collaborators" : "",
        hasPrivacyChanges ? "privacy" : "",
      ].filter(Boolean).join(" and ");
      toast.success(`Successfully updated ${changeDesc} for ${projectIds.length} project${projectIds.length !== 1 ? "s" : ""}`);

      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter(
            (user) =>
              !user.getUsername()?.includes("dummy_ommuse") &&
              user.getUsername() !== userName
          )
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setUsersList(
          res
            .getUsersList()
            .filter(
              (user) =>
                !user.getUsername()?.includes("dummy_ommuse") &&
                user.getUsername() !== userName
            )
        );
      }
    };
    if (open) {
      handleUserNames();
    }
  }, [enterprise?.id, getValidAccessToken, userName, open]);

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      style={{ marginTop: 100 }}
    >
      <ModalBody style={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        <Box sx={shareModalStyles.shareIconContainer}>
          <img
            style={shareModalStyles.shareIcons}
            src="/assets/icons/TracksPageIcons/users-plus.svg"
            alt="icon"
          />
        </Box>
        <Typography
          sx={{ color: TEXT_COLOR, marginBottom: "20px" }}
          variant="h6"
          fontFamily={FONT_FAMILY}
        >
          Edit {selectedProjectIds.size} Project{selectedProjectIds.size !== 1 ? "s" : ""}
        </Typography>
        <IconButton
          onClick={onClose}
          style={styles.postMessageModalIcon}
        >
          <CloseIcon style={{ color: APP_COLOR }} />
        </IconButton>

        {/* Privacy Section */}
        <Box
          sx={{
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={privacyEnabled}
                onChange={(e) => setPrivacyEnabled(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ color: TEXT_COLOR, fontFamily: FONT_FAMILY, fontSize: "14px" }}>
                Change Privacy Setting
              </Typography>
            }
          />
          {privacyEnabled && (
            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <CustomButton
                text="Make Private"
                onClick={() => setMakePrivate(true)}
                customStyles={{
                  width: "48%",
                  minWidth: "120px",
                  opacity: makePrivate ? 1 : 0.5,
                }}
              />
              <CustomButton
                text="Make Public"
                onClick={() => setMakePrivate(false)}
                customStyles={{
                  width: "48%",
                  minWidth: "120px",
                  opacity: !makePrivate ? 1 : 0.5,
                }}
              />
            </Box>
          )}
        </Box>

        {/* Collaborators Section */}
        <Typography
          sx={{ color: TEXT_COLOR, marginBottom: "10px", fontFamily: FONT_FAMILY, fontSize: "14px" }}
        >
          Add Collaborators
        </Typography>
        <Box sx={collaboratorsModalStyles.userNameLabelContainer}>
          <Box sx={{ width: "80%" }}>
            <label
              htmlFor="newCollaboratorUser"
              style={styles.modalCreatorUsername}
            >
              {"Collaborator's Username"}
            </label>
            <UsernameSelectField
              freeSolo
              forcePopupIcon={false}
              isOptionEqualToValue={(option, value) => option === value}
              inputValue={collabVal}
              onInputChange={(
                event: React.SyntheticEvent<Element, Event>,
                value: string
              ) => {
                setCollabVal(value.trim());
              }}
              options={userNames.filter(
                (name) => !collaborators.some((c) => c.getUsername() === name)
              )}
              renderInput={(params) => (
                <TextField
                  color="primary"
                  style={{
                    backgroundColor: APP_BACKGROUND_COLOR,
                    color: TEXT_COLOR,
                  }}
                  {...params}
                  label=" "
                  InputLabelProps={{
                    shrink: false,
                  }}
                  classes={{ root: classes.customTextField }}
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  {option}
                </li>
              )}
            />
          </Box>
          <CustomButton
            isDisabled={collabVal === ""}
            text="Add"
            onClick={() => addCollaborator(collabVal)}
            customStyles={{
              ...collaboratorsModalStyles.customButtonStyles,
              color: "black",
              "&.Mui-disabled": { color: "black", opacity: 0.4 },
            }}
          />
        </Box>

        <Box style={collaboratorsModalStyles.collaboratorContainer}>
          <Box>
            {collaborators &&
              collaborators.length > 0 &&
              collaborators.map((collaborator, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <img
                    style={collaboratorsModalStyles.profilePic}
                    src={
                      collaborator.getProfilePicture()
                        ? collaborator.getProfilePicture()
                        : enterprise?.image
                    }
                    alt={collaborator.getUsername()}
                  />
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1, color: TEXT_COLOR }}>
                    {collaborator.getUsername()}
                  </p>
                  <IconButton onClick={() => removeCollaborator(collaborator)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        <CustomButton
          text="Confirm"
          loading={loading}
          onClick={handleConfirm}
          isDisabled={collaborators.length === 0 && !privacyEnabled}
          customStyles={{ width: "100%", color: "black", "&.Mui-disabled": { color: "black", opacity: 0.4 } }}
        />
      </ModalBody>
    </Modal>
  );
};

export default BatchEditProjectModal;
